import { useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { findIndex } from "lodash-es";
import { useTranslation } from "react-i18next";

import type {
  FormContextData,
  FormContextMiniForm,
} from "@thalamos/aspire-common";
import {
  formHasSignatures,
  formIsCompleted,
  formIsInProgress,
  getBaseFormTemplate,
  getFullFormName,
} from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import { MultiPagePdf } from "../../../components/MultiPagePdf.js";
import { ukLocalFormatDateTime } from "../../../util.js";
import {
  Button,
  ButtonIcon,
  FormTitle,
  LoadingSpinner,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";

/**
 * Returns a human-readable title for police incident form templates that are
 * not registered in the shared `baseTemplates` list (i.e. `mha-136` and
 * `mca-police`). Returns an empty string for all other template IDs.
 */
const POLICE_FORM_TITLES: Partial<
  Record<string, (t: ReturnType<typeof useTranslation>["t"]) => string>
> = {
  "mha-136": (t) =>
    t("pages.policeIncidentFormPage.formTitles.policeIncident_s136"),
  "mca-police": (t) =>
    t("pages.policeIncidentFormPage.formTitles.policeIncident_mca"),
};

function getPoliceFormTitle(
  templateId: string,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  return POLICE_FORM_TITLES[templateId]?.(t) ?? "";
}

interface PdfViewerContentsProps {
  formId: string;
  setFormId: (params: any) => void;
  inModal: boolean;
  data: string | null;
  handlePrev: () => void;
  handleNext: () => void;
  shouldBeDisabledPrev: boolean;
  shouldBeDisabledNext: boolean;
  currentForm: FormContextMiniForm;
  showPrevNextButtons?: boolean;
  hideUploadedFormInfo?: boolean;
}

const PdfViewerContents = ({
  data,
  handlePrev,
  handleNext,
  shouldBeDisabledPrev,
  shouldBeDisabledNext,
  showPrevNextButtons,
  currentForm,
  hideUploadedFormInfo,
}: PdfViewerContentsProps) => (
  <Box>
    <Box
      sx={{
        width: "100%",
        position: "sticky",
        top: "0",
        display: "flex",
        justifyContent: "space-around",
        zIndex: 99,
        overflowY: "auto",
        background: "white",
        padding: 1,
      }}
    >
      <Box>
        {showPrevNextButtons && (
          <Button
            disabled={shouldBeDisabledPrev}
            label={"Previous Form"}
            startIcon={ButtonIcon.arrowBack}
            onClick={handlePrev}
          />
        )}
      </Box>
      <Box>
        <Box sx={{ mr: 8 }}>
          {showPrevNextButtons && (
            <Button
              disabled={shouldBeDisabledNext}
              label={"Next Form"}
              endIcon={ButtonIcon.arrow}
              onClick={handleNext}
            />
          )}
        </Box>
      </Box>
    </Box>
    {currentForm?.source === "manual-upload" && !hideUploadedFormInfo && (
      <UploadedFormInfo created={currentForm.created} />
    )}
    <Box
      sx={{
        marginLeft: 2,
        marginRight: 2,
        minHeight: "90vh",
        display: "flex",
        justifyContent: "center",
      }}
    >
      {data ? <MultiPagePdf data={data} /> : <LoadingSpinner />}
    </Box>
  </Box>
);

/**
 * The forms in a context that have something worth previewing — anything signed
 * and not since deleted — newest first.
 */
export function previewableForms(
  forms: FormContextData["forms"],
): FormContextData["forms"] {
  return forms
    .filter(formHasSignatures)
    .filter((f) => formIsCompleted(f) || formIsInProgress(f))
    .sort((f1, f2) => (f1.updated < f2.updated ? 1 : -1));
}

export function FormContextPdfViewer({
  formId,
  setFormId,
  forms,
  inModal,
  hideUploadedFormInfo,
}: {
  forms: FormContextData["forms"];
  formId: string;
  setFormId: (formId: string | null) => void;
  inModal: boolean;
  hideUploadedFormInfo?: boolean;
}) {
  const { t } = useTranslation();
  const [data, setData] = useState<null | string>(null);

  useEffect(() => {
    setData(null);
    api.forms.getPdf(formId, false).then((result) => {
      setData(result.data!);
    });
  }, [formId]);

  const currentFormIndex = findIndex(forms, (f) => f.id === formId);

  const currentForm = forms[currentFormIndex];

  // Defensive: if asked to view a form that is not in the provided list (e.g. an
  // in-progress form with no PDF), don't crash — render nothing and let the
  // caller correct the selection.
  if (!currentForm) {
    return null;
  }

  const template = getBaseFormTemplate(currentForm.formTemplate.id);

  const fullFormName =
    getFullFormName(template) ||
    getPoliceFormTitle(currentForm.formTemplate.id, t);

  const previousForm =
    currentFormIndex > 0 ? forms[currentFormIndex - 1] : null;
  const nextForm =
    currentFormIndex < forms.length - 1 ? forms[currentFormIndex + 1] : null;

  return inModal ? (
    <PopupDialog open={true} onClose={() => setFormId(null)} hasPadding={false}>
      <Box sx={{ my: 2, mx: 7.5 }}>
        <PopupDialogTitle
          titleText={fullFormName || ""}
          subtitleText="View PDF"
          closeDialog={() => setFormId(null)}
          removeMarginBottom
        />
      </Box>
      <PdfViewerContents
        formId={formId}
        setFormId={setFormId}
        inModal={inModal}
        data={data}
        handlePrev={() => setFormId(previousForm?.id || null)}
        handleNext={() => setFormId(nextForm?.id || null)}
        shouldBeDisabledPrev={!previousForm}
        shouldBeDisabledNext={!nextForm}
        showPrevNextButtons={forms.length > 1}
        currentForm={currentForm}
        hideUploadedFormInfo={hideUploadedFormInfo}
      />
      <Box
        display="flex"
        justifyContent={"center"}
        sx={{
          mb: 3,
          width: "100%",
          position: "sticky",
          bottom: "0",
          display: "flex",
          justifyContent: "space-around",
          zIndex: 99,
          overflowY: "auto",
          background: "white",
          padding: 1,
        }}
      >
        <Button
          variant="outlined"
          label="Close"
          testId="close-button"
          onClick={() => setFormId(null)}
        />
      </Box>
    </PopupDialog>
  ) : (
    <>
      <PdfViewerContents
        formId={formId}
        setFormId={setFormId}
        inModal={inModal}
        data={data}
        handlePrev={() => setFormId(previousForm?.id || null)}
        handleNext={() => setFormId(nextForm?.id || null)}
        shouldBeDisabledPrev={!previousForm}
        shouldBeDisabledNext={!nextForm}
        showPrevNextButtons={forms.length > 1}
        currentForm={currentForm}
        hideUploadedFormInfo={hideUploadedFormInfo}
      />
    </>
  );
}

export function VersionFormsPdfViewer({
  formId,
  setFormId,
  forms,
  inModal,
  showPrevNextButtons = true,
}: {
  forms: FormContextData["forms"];
  formId: string;
  setFormId: (formId: string | null) => void;
  inModal: boolean;
  showPrevNextButtons?: boolean;
}) {
  const [data, setData] = useState<null | string>(null);

  const currentForm = forms.find((f) => f.id === formId);

  const currentFormVersion = currentForm?.latestVersion;

  const [currentVersion, setCurrentVersion] = useState(currentFormVersion);

  const { t } = useTranslation();

  useEffect(() => {
    setData(null);
    api.forms.getPdf(formId, false, [], currentVersion).then((result) => {
      setData(result.data!);
    });
  }, [formId, currentVersion]);

  const findVersionByOffset = (offset: number) => {
    const targetVersion = currentVersion! + offset;
    return currentForm?.versions.find(
      (version) => version.version === targetVersion,
    );
  };

  const handleVersionChange = (offset: number) => {
    const findVersion = findVersionByOffset(offset);
    if (findVersion) {
      setCurrentVersion(findVersion.version);
    }
  };

  const handlePrevForm = () => handleVersionChange(-1);
  const handleNextForm = () => handleVersionChange(1);

  const indexOfCurrentVersion = currentForm?.versions.findIndex(
    (version) => version.version === currentVersion,
  );

  const shouldBeDisabledNext =
    !currentForm?.versions[indexOfCurrentVersion! + 1];

  const shouldBeDisabledPrev =
    !currentForm?.versions[indexOfCurrentVersion! - 1];

  const isCurrentVersion = currentFormVersion === currentVersion;

  const template = currentForm?.formTemplate
    ? getBaseFormTemplate(currentForm.formTemplate.id)
    : null;

  const policeFormTitle = currentForm?.formTemplate
    ? getPoliceFormTitle(currentForm.formTemplate.id, t)
    : "";
  const fullFormName =
    (template ? getFullFormName(template) : null) || policeFormTitle || null;

  return inModal ? (
    <PopupDialog open={true} onClose={() => setFormId(null)} hasPadding={false}>
      <Box sx={{ mt: 2, mx: 8 }}>
        <PopupDialogTitle
          titleText={fullFormName || ""}
          subtitleText="View PDF"
          closeDialog={() => setFormId(null)}
          removeMarginBottom
        />
      </Box>

      {currentFormVersion! > 1 && (
        <>
          <Box
            display="flex"
            sx={{
              ml: 6,
              mr: 6,
              mt: 1,
              border: 3,
              borderColor: isCurrentVersion
                ? "primary.lightGreen"
                : "error.darker",
            }}
          >
            <Box display="flex" sx={{ p: 2 }}>
              <FormTitle
                useReducedTopPadding={true}
                hasTitleBottomMargin={false}
                titleText={`${
                  isCurrentVersion ? "Current Version" : "Old Version"
                }`}
                subtitleText={`Version ${currentVersion} of ${currentFormVersion}`}
              />
            </Box>
          </Box>
        </>
      )}

      <PdfViewerContents
        formId={formId}
        setFormId={setFormId}
        inModal={inModal}
        data={data}
        handlePrev={handlePrevForm}
        handleNext={handleNextForm}
        shouldBeDisabledPrev={shouldBeDisabledPrev}
        shouldBeDisabledNext={shouldBeDisabledNext}
        showPrevNextButtons={currentFormVersion! > 1 && showPrevNextButtons}
        currentForm={currentForm!}
      />
      <Box
        display="flex"
        justifyContent={"center"}
        sx={{
          width: "100%",
          position: "sticky",
          bottom: "0",
          display: "flex",
          justifyContent: "space-around",
          zIndex: 99,
          overflowY: "auto",
          background: "white",
          padding: 1,
        }}
      >
        <Button
          variant="outlined"
          label={t("buttonLabels.close")}
          testId="close-button"
          onClick={() => setFormId(null)}
        />
      </Box>
    </PopupDialog>
  ) : (
    <PdfViewerContents
      formId={formId}
      setFormId={setFormId}
      inModal={inModal}
      data={data}
      handlePrev={handlePrevForm}
      handleNext={handleNextForm}
      shouldBeDisabledPrev={shouldBeDisabledPrev}
      shouldBeDisabledNext={shouldBeDisabledNext}
      showPrevNextButtons={
        currentForm?.latestVersion! > 1 && showPrevNextButtons
      }
      currentForm={currentForm!}
    />
  );
}

function UploadedFormInfo({ created }: { created: string }) {
  const theme = useTheme();

  return (
    <Typography
      textAlign="center"
      sx={{ mt: 1 }}
      fontWeight="bold"
      color={theme.palette.error.main}
    >
      Form Uploaded:
      {ukLocalFormatDateTime(created)}
    </Typography>
  );
}
